import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper, Typography, useTheme, Stack, Divider, Button, Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { isNil } from 'lodash'
import { WallboxModel, WallboxType } from '../models/wallbox-model'
import { getCurrencyValue } from '../../shared/utils/currency-value'
import { TariffsStatusChip } from '../../shared/components/TariffsStatusChip'
import { WallboxMenu } from './WallboxMenu'
import useModals from 'src/Hooks/useModals'
import { useHagerBridgePermissions } from '../../shared/hooks/use-hager-bridge-permissions'
import { useNavigate } from 'react-router-dom'
import { getCurrentDynamicPrices } from '../../tariffs/utils/get-current-dynamic-prices'
import { DynamicPriceIcon } from '../../shared/icons/DynamicPriceIcon'
import { StaticPriceIcon } from '../../shared/icons/SaticPriceIcon'
import { TariffCategory, TariffStatusType } from '../../shared/models/types'
import { getWallboxSolarPrice } from '../utils/get-wallbox-solar-price'
import { TariffChart } from '../../tariffs/components/tariff-details/tariff-chart/TariffChart'
import { TariffDetailsModel } from '../../tariffs/models/single-tariff-model'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'
import { useDispatch } from 'react-redux'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(0),
    flex: 1,
    height: '100%',
  },
  titleContainer: {
    padding: theme.spacing(1),
    paddingRight: theme.spacing(0),
    borderLeft: `4px solid ${theme.palette.background.paperAccent}`,
  },
  priceContainer: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.shades.shade04,
    marginTop: theme.spacing(3),
  },
  divider: {
    borderColor: theme.palette.divider,
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  noTariffsContainer: {
    height: '255px',
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  midMeterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.shades.shade04,
    marginTop: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
}))

interface WallboxCardProps {
  wallbox: WallboxModel
  installationId: number
  shouldDisplayTariffChart?: boolean
}

/**
 *  Represents single wallbox card component.
 *  @param wallbox - single WallboxModel
 *  @param installationId - unique installation identifier as string
 *  @param user - user data from account link which match logged user
 */
export const WallboxCard: React.FC<WallboxCardProps> = ({ wallbox, installationId, shouldDisplayTariffChart }): JSX.Element => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { permissions } = useHagerBridgePermissions()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { openTariffDetailsDialog } = useModals()

  const isDynamicPrice = wallbox.tariffToDisplay?.tariffCategory === TariffCategory.dynamicTariff
  const tariffId = wallbox.tariffToDisplay?.tariffId || ''
  const currencyCode = wallbox.tariffToDisplay?.currencyCode

  const currentDynamicGridPrice = wallbox.tariffToDisplay && getCurrentDynamicPrices(wallbox.tariffToDisplay.dynamicPrices)?.gridPrice
  const solarPrice = getWallboxSolarPrice(wallbox)
  const formattedSolarPrice = !isNil(solarPrice) && currencyCode ? getCurrencyValue(solarPrice, currencyCode) : '-'

  const tariffChartDetails: TariffDetailsModel = {
    tariffName: wallbox.tariffToDisplay?.tariffName ?? '',
    currencyCode: wallbox.tariffToDisplay?.currencyCode ?? 'EUR',
    dynamicPrices: wallbox.tariffToDisplay?.dynamicPrices ?? [],
    dischargePrice: wallbox.tariffToDisplay?.dischargePrice ?? 0,
    solarPrice: solarPrice ?? 0,
    staticGridPrice: currentDynamicGridPrice ?? 0,
    tariffStatus: wallbox.tariffToDisplay?.tariffStatus ?? TariffStatusType.active,
    tariffCategory: TariffCategory.dynamicTariff,
    endTime: wallbox.tariffToDisplay?.endTime ?? '',
    lastUpdated: '',
    startTime: wallbox.tariffToDisplay?.startTime ?? '',
  }

  return (
    <Paper className={classes.container} id="wallbox-card">
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.titleContainer}>
        <Stack direction={'row'} alignItems={'center'}>
          <Typography variant={theme.typography.dialog} color="textPrimary">
            {t('emobility.wallbox.cardTitle', { wallbox: wallbox.wallboxName, id: wallbox.wallboxId })}
          </Typography>
          {isDynamicPrice ? <DynamicPriceIcon style={{ marginLeft: 5, marginBottom: 5 }} /> : <StaticPriceIcon style={{ marginBottom: 7 }} />}
        </Stack>

        {permissions.wallboxPermissions.assignTariff || permissions.wallboxPermissions.assignToken ? (
          <WallboxMenu wallbox={wallbox} installationId={installationId} />
        ) : (
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              dispatch(updateChargingSessionsFilters({ wallboxIds: [wallbox.wallboxId], page: 0 }))
              navigate(`/e-mobility/${installationId}/charging-sessions`)
            }}
          >
            {t('emobility.wallbox.view-sessions')}
          </Button>
        )}
      </Stack>
      <Grid container spacing={shouldDisplayTariffChart ? 3 : 0}>
        <Grid item xs={12} sm={shouldDisplayTariffChart ? 6 : 12}>
          <Stack className={classes.midMeterContainer}>
            {wallbox.midMeter ? (
              <>
                <Typography>{t('emobility.wallbox.mid-meter')}</Typography>
                <Typography fontWeight="500">{t(`emobility.wallbox.mid-meter-status-${wallbox.midMeter}`)}</Typography>
              </>
            ) : (
              <>
                <Typography>{t('emobility.wallbox.mid-meter')}</Typography>
                <Typography fontWeight="500">{t('emobility.wallbox.mid-meter-not-configured')}</Typography>
              </>
            )}
          </Stack>
          {wallbox.tariffToDisplay ? (
            <Stack id="wallbox-card-mobile" className={classes.priceContainer}>
              <Stack className={classes.rowContainer} id={`wallbox-card-tariff-name`}>
                <Typography>{t('emobility.wallbox.tariffName')}</Typography>
                <Typography fontWeight="500">{wallbox.tariffToDisplay.tariffName}</Typography>
              </Stack>
              <Divider className={classes.divider} />
              <Stack className={classes.rowContainer} id={`wallbox-card-grid-price`}>
                <Typography>{isDynamicPrice ? t('emobility.wallbox.current-grid-price-per-kwh') : t('emobility.wallbox.pricePerKwh')}</Typography>
                <Typography fontWeight="500">
                  {isDynamicPrice &&
                    (currentDynamicGridPrice ? getCurrencyValue(currentDynamicGridPrice, wallbox.tariffToDisplay.currencyCode) : '-')}
                  {!isDynamicPrice && getCurrencyValue(wallbox.tariffToDisplay?.staticPrices.gridPrice, wallbox.tariffToDisplay.currencyCode)}
                </Typography>
              </Stack>
              <Divider className={classes.divider} />
              <Stack className={classes.rowContainer} id={`wallbox-card-solar-price`}>
                <Typography>{t('emobility.wallbox.solar-price')}</Typography>
                <Typography fontWeight="500">{formattedSolarPrice}</Typography>
              </Stack>
              <Divider className={classes.divider} />
              {wallbox.wallboxType === WallboxType.Bidi && (
                <>
                  <Stack className={classes.rowContainer}>
                    <Typography>{t('emobility.wallbox.dischargePricePerKwh')}</Typography>
                    <Typography fontWeight="500">
                      {getCurrencyValue(wallbox.tariffToDisplay.dischargePrice, wallbox.tariffToDisplay.currencyCode)}
                    </Typography>
                  </Stack>
                  <Divider className={classes.divider} />
                </>
              )}

              <Stack className={classes.rowContainer}>
                <Typography>{t('emobility.wallbox.tariffStatus')}</Typography>
                <TariffsStatusChip status={wallbox.tariffToDisplay.tariffStatus} />
              </Stack>
              {permissions.tariffPermissions.viewTariffs && !shouldDisplayTariffChart && (
                <>
                  <Divider className={classes.divider} />
                  <Stack flexDirection={'row'} p={2}>
                    <Button variant="text" size="medium" onClick={() => openTariffDetailsDialog({ installationId, tariffId })}>
                      {t('emobility.wallbox.show-more-details')}
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          ) : (
            <NoTariffs wallbox={wallbox} installationId={installationId} />
          )}
        </Grid>
        {shouldDisplayTariffChart && (
          <Grid item xs={12} sm={6}>
            <Stack height="100%">
              <Typography variant="body2" width={'100%'} fontWeight={500}>
                {t('emobility.tariffs.price-details')}
              </Typography>
              <TariffChart tariff={tariffChartDetails} height={330} />
            </Stack>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}

/**
 *  Represents component to display if wallbox has no tariffs assigned.
 *  @param wallbox - single WallboxModel
 *  @param installationId - unique installation identifier as string
 */
const NoTariffs: React.VFC<WallboxCardProps> = ({ wallbox, installationId }): JSX.Element => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { openAssignTariffDialog } = useModals()
  const { permissions } = useHagerBridgePermissions()

  return (
    <Stack className={classes.noTariffsContainer}>
      <Typography mb={2}>{t('emobility.wallbox.no-tariff-set')}</Typography>
      {permissions.wallboxPermissions.assignTariff && (
        <Button variant="outlined" onClick={() => openAssignTariffDialog({ wallbox, installationId })}>
          {t('emobility.wallbox.assign-tariff')}
        </Button>
      )}
    </Stack>
  )
}
